import { WalletConfig, WalletField } from "@/types/DocumentTemplate";
import {
  BarcodeBlock,
  emptyValueMark,
  FieldRow,
  PassCard,
  useDeviceKeepsRow,
  useTemplateRenderer,
  useColors,
  useDeviceOnly,
  usePassCertaintyRows,
} from "./sharedPreviewBits";
import type { FieldGroup } from "../passRenderRules";

type Props = {
  config: WalletConfig;
  currentLanguage: string;
  /**
   * Art. 50 Abs. 2 KI-VO (plan D9): the AI mode of this pass — the saved mark,
   * a save of this session that carried an AI tool, or an open AI change.
   * Set ⇒ the card carries the AI notice module, exactly like the consumer's
   * `buildGenericObject` (B1). Absent / null ⇒ no notice, card unchanged.
   */
  aiDisclosureMode?: GoogleAiDisclosureMode | null;
};

/** IETF vocabulary, as the consumer's `walletAiDisclosureMode` (plan "Vokabular"). */
export type GoogleAiDisclosureMode = "ai-originated" | "ai-modified";

/**
 * The AI notice's id — `AI_DISCLOSURE_MODULE_ID` in main-consumer's
 * google-wallet-service.ts. Not a front-row id, so Google draws it on the
 * details sheet; the consumer pushes it LAST, after every template row.
 */
export const GOOGLE_AI_DISCLOSURE_MODULE_ID = "ai_disclosure";

/**
 * The notice's neutral wording — byte-identical to `AI_DISCLOSURE_CARD_TEXT` in
 * main-consumer's google-wallet-service.ts (plan D9: literals, not i18n keys;
 * the cross-repo case in `GooglePassPreview.aiDisclosure.test.tsx` compares
 * them when that repo is checked out beside this one).
 */
export const GOOGLE_AI_DISCLOSURE_CARD_TEXT = {
  de: { header: "KI-Hinweis", body: "Kann mit KI erstellte oder bearbeitete Inhalte enthalten." },
  en: { header: "AI notice", body: "May contain content created or edited with AI." },
} as const;

/**
 * The notice module for `mode` in the card's language — the consumer's
 * `aiDisclosureTextModule`: `de*` ⇒ German, every other language English, the
 * machine-readable `AI-Disclosure: mode=…` after the sentence.
 */
export function googleAiDisclosureModule(
  mode: GoogleAiDisclosureMode,
  lang: string,
): { id: string; header: string; body: string } {
  const base = (lang ?? "").trim().toLowerCase().split(/[-_]/)[0];
  const text = base === "de" ? GOOGLE_AI_DISCLOSURE_CARD_TEXT.de : GOOGLE_AI_DISCLOSURE_CARD_TEXT.en;
  return {
    id: GOOGLE_AI_DISCLOSURE_MODULE_ID,
    header: text.header,
    body: `${text.body} AI-Disclosure: mode=${mode}`,
  };
}

// Faithful preview of a Google Wallet GenericObject as `buildGenericObject` in
// main-consumer's google-wallet-service.ts ships it.
//
// We set no `classTemplateInfo`, so Google draws the object with its DEFAULT
// template (developers.google.com/wallet/generic/resources/template), the
// centred design, top to bottom:
//
//   1. logo (a circle), cardTitle (small), header (large)
//   2. the barcode and its alternate text
//   3. two rows of two text modules — the OBJECT's modules with the ids
//      `row2left` / `row2right` / `row3left` / `row3right`, label over value,
//      the right-hand one right-aligned
//   4. the hero image, last, at the card's full width
//
// and UNDER the card a details sheet with every other text module. The consumer
// gives those four ids to the first four front modules it actually DELIVERS;
// back fields never get one. This component hands out the same four slots by
// the same rule — survivors first, slots second — so the FRONT agrees with the
// card by construction. The sheet agrees up to the consumer's cap of
// `GOOGLE_MAX_TEXT_MODULES` delivered modules, which is mirrored below.
//
// What is NOT settled from Google's documentation and needs a phone: whether a
// row with a single module spans the card's full width (the page says such a
// row is "displayed as a row with fewer items"; here it keeps its column), and
// what the area under the card looks like in the current app design. Google
// Wallet apps that have not received the centred redesign still draw the
// previous left-aligned card. Nothing here models that either.
//
// This differs hard from Apple, so the builder shows it under the Google toggle
// instead of pretending one layout serves both.

/**
 * How many of the object's text modules the default template puts on the front.
 * Mirrors `GOOGLE_FRONT_ROW_IDS.length` in main-consumer's
 * google-wallet-service.ts; the layout test compares the two when that repo is
 * checked out beside this one.
 */
export const GOOGLE_FRONT_SLOTS = 4;

/**
 * OUR cap on delivered text modules — `GOOGLE_MAX_TEXT_MODULES` in the same
 * consumer file, which stops there and says so only in the worker's log. Same
 * cross-repo pin as above.
 */
export const GOOGLE_MAX_TEXT_MODULES = 10;

/** Google's details sheet is its own chrome: white in both app themes. */
const SHEET_COLORS = { bg: "#ffffff", fg: "#1f1f1f", lbl: "#5f6368" };

type Entry = { field: WalletField; group: FieldGroup; gi: number };

export function GooglePassPreview({ config, currentLanguage, aiDisclosureMode }: Props) {
  const colors = useColors(config);
  const deviceOnly = useDeviceOnly();
  const certaintyRows = usePassCertaintyRows();
  const { render, resolve, text } = useTemplateRenderer();
  const deviceKeepsRow = useDeviceKeepsRow(currentLanguage);

  // Resolved like a field, not by exact index: `text` walks the same tiers
  // `FieldRow` does, so a card whose only column is `de-DE` still has a title
  // under `de`.
  const orgText = text(config.organizationName, currentLanguage);
  const descText = text(config.description, currentLanguage);
  // The DEVICE view mirrors the consumer's fallback chain, because cardTitle and
  // header are REQUIRED on a GenericObject and it never ships either one empty:
  // `title = org || description || "Pass"`, `header = description || title`.
  // The builder view keeps its two stand-ins, which name an empty slot.
  const shipped = (raw: string) => (raw && resolve(raw) ? raw : "");
  const deviceTitle = shipped(orgText) || shipped(descText) || "Pass";
  const deviceHeader = shipped(descText) || deviceTitle;
  const cardTitle = deviceOnly ? deviceTitle : orgText;
  const header = deviceOnly ? deviceHeader : descText;

  // Apple's five field sections all collapse into one flat textModules list.
  //
  // The GROUP each module came from is kept alongside it. Flattening loses it
  // from the array's shape, not from this function — and it must not be lost,
  // because field keys are minted per group: `nextFieldKey` numbers within one
  // section, so `field_1` in `headerFields` AND in `backFields` is the ordinary
  // shape of an editor-built pass. Asking a flat key set here let a row the
  // client could not decide in one group answer for a perfectly good row in
  // another — and on the `absent` verdict that DELETED it.
  //
  // `gi` is the position in THIS list and rides along with the entry. The rows
  // are filtered and split three ways below, so by the time a `FieldRow` asks
  // about "its field 0" that index means nothing here any more.
  const grouped: Entry[] = [
    ...(config.headerFields ?? []).map((f) => ({ field: f, group: "headerFields" as const })),
    ...(config.primaryFields ?? []).map((f) => ({ field: f, group: "primaryFields" as const })),
    ...(config.secondaryFields ?? []).map((f) => ({ field: f, group: "secondaryFields" as const })),
    ...(config.auxiliaryFields ?? []).map((f) => ({ field: f, group: "auxiliaryFields" as const })),
    ...(config.backFields ?? []).map((f) => ({ field: f, group: "backFields" as const })),
  ]
    // The consumer's FIRST rule: `buildGenericObject` skips a field whose key
    // is blank before it looks at anything else, so such a field never takes a
    // slot or a share of the cap. Both rule passes in `WalletPreview` strip
    // these upstream today; this keeps the mirror complete without relying on
    // that.
    .filter((e) => !!e.field.key?.trim())
    .map((e, gi) => ({ ...e, gi }));
  // By position in `grouped`, not by key: two modules can legitimately carry the
  // same key from two different sections, and this is the lane where that is
  // normal rather than exotic.
  const certaintyAt = (gi: number) => {
    const entry = grouped[gi];
    const key = entry?.field.key;
    return entry && key ? certaintyRows[entry.group]?.[key] : undefined;
  };
  const certaintyIn = (entries: Entry[]) => (_field: WalletField, i: number) => {
    const entry = entries[i];
    return entry ? certaintyAt(entry.gi) : undefined;
  };

  // Survivors FIRST, slots second — the consumer's order. In the device view a
  // row the card will not carry is gone before the four slots are handed out,
  // so the next front field moves up exactly as it does on the phone. The
  // builder view keeps every configured row (`deviceKeepsRow` answers true).
  const delivered = grouped.filter((e) =>
    deviceKeepsRow(
      e.field,
      emptyValueMark({
        deviceOnly,
        certainty: certaintyAt(e.gi),
        labelColor: colors.lbl,
      }),
    ),
  );
  const front = delivered.filter((e) => e.group !== "backFields");
  const slots = front.slice(0, GOOGLE_FRONT_SLOTS);
  // Everything else: front fields past the fourth, then every back field.
  const sheet = [
    ...front.slice(GOOGLE_FRONT_SLOTS),
    ...delivered.filter((e) => e.group === "backFields"),
  ];
  const hasSlots = slots.length !== 0;
  // Art. 50 KI-VO (plan D9): a marked card carries the AI notice as its LAST
  // module, and the template's own rows run into a cap one lower so the notice
  // always fits — the consumer's `templateModuleCap`.
  const aiNotice = aiDisclosureMode ? googleAiDisclosureModule(aiDisclosureMode, currentLanguage) : null;
  const templateModuleCap = aiNotice ? GOOGLE_MAX_TEXT_MODULES - 1 : GOOGLE_MAX_TEXT_MODULES;
  const hasTemplateSheet = sheet.length !== 0;
  const hasSheet = hasTemplateSheet || aiNotice !== null;

  // Shared by both `FieldRow` lanes. Each is the same argument made once:
  //  - no dateStyle: a text module takes a string, and the consumer ships the
  //    substituted text. Formatting here would draw a card Google never does.
  //  - no textAlignment: a module is `{id, header, body}`; where it sits is
  //    decided by its row id and nothing else.
  //  - no shrinking, and WRAPPING instead: Google wraps a long body, it has
  //    no type-fitting step. `fit={false}` stays spelled out beside
  //    `wrapValue` — `fitTextCallSites.test.tsx` pins it in this file.
  //  - not Apple's measured 20pt either; nothing has been measured for a
  //    Google text module, so this lane keeps the size it always had.
  return (
    <div className="w-[320px] flex flex-col gap-3">
      <PassCard bg={colors.bg} autoHeight>
        {/* Its own header on purpose, and deliberately WITHOUT the header-row
            collision warning that PassHeader carries. That warning is about a
            PassKit-specific fact: Apple puts the brand line and the header
            fields in one unwrapped row where they can overlap. Google has no
            such row, so there is nothing to collide. Do not "fix" this by
            routing the Google card through PassHeader; it would warn about a
            layout this platform never draws. */}
        <div className="px-4 pt-5 pb-2 flex flex-col items-center text-center">
          {config.logoUrl && (
            <img
              src={config.logoUrl}
              alt="Logo"
              className="h-12 w-12 rounded-full object-cover mb-2"
            />
          )}
          <div className="max-w-full text-[11px] truncate" style={{ color: colors.lbl }}>
            {/* Gated in the device view for the same reason "Organization"
                is on the Apple card: it is a builder affordance naming an
                empty slot, and a Google object never carries those words. */}
            {cardTitle ? render(cardTitle) : deviceOnly ? null : "Card title"}
          </div>
          {/* Google renders `header` as the dominant headline (≠ a footer). */}
          <div
            className="max-w-full text-lg font-semibold leading-tight line-clamp-2"
            style={{ color: colors.fg }}
          >
            {header ? render(header) : deviceOnly ? null : "Header"}
          </div>
        </div>

        {/* Directly under the title block, as Google places it. `BarcodeBlock`
            carries an `mt-auto` for Apple's fixed-height face; in an
            auto-height card there is no spare height for it to claim. */}
        <BarcodeBlock config={config} colors={colors} />

        {hasSlots && (
          <div
            data-testid="google-front-rows"
            className="px-4 pt-1 pb-4 grid grid-cols-2 gap-x-4 gap-y-3"
          >
            {slots.map((slot, k) => {
              // row2left, row2right, row3left, row3right — odd slots are the
              // right-hand column, which Google right-aligns. A CLASS on the
              // wrapper, never a `text-align` style: nothing here is the
              // field's own `textAlignment` being honoured.
              const right = k % 2 === 1;
              return (
                <div key={slot.gi} className={right ? "min-w-0 text-right" : "min-w-0"}>
                  {/* One field per `FieldRow`. Its `layout="row"` is Apple's
                      measured content-sized row and cannot draw two equal
                      columns; the grid does that, the row only places its
                      single cell at the start or the end. */}
                  <FieldRow
                    fields={[slot.field]}
                    certaintyOf={certaintyIn([slot])}
                    currentLanguage={currentLanguage}
                    colors={colors}
                    justify={right ? "end" : "between"}
                    deviceFormatsDates={false}
                    honourAlignment={false}
                    fit={false}
                    wrapValue
                    valueClassName="text-sm font-medium"
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* LAST, inside the coloured card, at full width and in the image's own
            aspect: the REST reference says the hero is "displayed at 100%
            width", so nothing is cut off its sides. That the height simply
            follows the image is an inference from Google's 1032×812 guideline,
            not a quoted rule — check on a phone. The cockpit's crop delivers
            Apple's strip aspect (3.83:1 for an event ticket, 2.60:1 for a
            coupon), so that is the shape drawn here. */}
        {config.stripImageUrl && (
          <img src={config.stripImageUrl} alt="Hero" className="block w-full h-auto" />
        )}
      </PassCard>

      {/* The details sheet Google shows under the card. White by INLINE style:
          not `bg-white` (e2e WLU05 finds the barcode plate by that class and
          must keep finding exactly one), and not a surface token (dark in the
          dark cockpit, while the text colours here are fixed). No heading —
          Google's own chrome is not ours to imitate. */}
      {hasSheet && (
        <div
          data-testid="google-details-sheet"
          className="rounded-2xl shadow px-4 py-3"
          style={{ backgroundColor: SHEET_COLORS.bg }}
        >
          {hasTemplateSheet && (
          <FieldRow
            fields={sheet.map((e) => e.field)}
            certaintyOf={certaintyIn(sheet)}
            // The consumer stops at GOOGLE_MAX_TEXT_MODULES delivered modules.
            // The front slots are handed out first, so whatever is left of the
            // budget is the sheet's — `slots.length`, NOT `front.length`: a
            // front field past the fourth is already one of the sheet's rows.
            // `FieldRow` does with it what it does with Apple's per-row caps:
            // the device view drops the surplus, the builder view dims and
            // tags it (`data-beyond-limit`) rather than hiding a configured
            // field. There is NO caption explaining the dimming yet —
            // `HiddenDataBanner` only knows Apple's limits. That needs a new
            // i18n key in every locale and is an open follow-up; the builder
            // view also counts CONFIGURED rows, so it is the worst case, and
            // the device toggle is the exact answer.
            // With the AI notice on the card the template rows get one slot
            // fewer (`templateModuleCap`), as the consumer caps them.
            visibleCount={templateModuleCap - slots.length}
            currentLanguage={currentLanguage}
            colors={SHEET_COLORS}
            layout="column"
            deviceFormatsDates={false}
            honourAlignment={false}
            fit={false}
            wrapValue
            valueClassName="text-sm font-medium"
          />
          )}
          {/* The AI notice — last on the sheet, after every template row, as
              the consumer pushes it. Header over body like any text module;
              its own markup because it is not a configured field (no key, no
              certainty, no beyond-limit dimming: it always ships). */}
          {aiNotice && (
            <div
              data-testid="google-ai-disclosure"
              data-module-id={aiNotice.id}
              className={hasTemplateSheet ? "mt-3 min-w-0" : "min-w-0"}
            >
              <div className="text-[11px] uppercase tracking-wide" style={{ color: SHEET_COLORS.lbl }}>
                {aiNotice.header}
              </div>
              <div className="text-sm font-medium break-words" style={{ color: SHEET_COLORS.fg }}>
                {aiNotice.body}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
